<script>
import { get } from "lodash";
import { mapState } from "vuex";
import api from "@/services/api";

export default {
  name: "proposal",
  data: () => ({
    params: null,
    currentStep: 1,
    proposal: null,
    logo: null,
    accessCode: "",
    privacyPolicy: false,
    termsOfUse: false,
    acceptReason: "",
    rejectReason: "",
    error: null,
  }),
  computed: {
    isFormValid() {
      return this.accessCode && this.privacyPolicy && this.termsOfUse;
    },
    isReasonValid() {
      return this.acceptReason && this.acceptReason.trim().length > 0;
    },
    isRejectReasonValid() {
      return this.rejectReason && this.rejectReason.trim().length > 0;
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return "Não Informado";
      
      try {
        const [year, month, day] = date.split('-');
        return `${day}/${month}/${year}`;
      } catch (error) {
        console.error("Erro ao formatar data:", error);
        return "Não Informado";
      }
    },

    async fetchProposal() {
      try {
        const params = get(this.$router, "history.current.params", null);
        this.params = params;
        this.getProposal();
      } catch (error) {
        console.error(error);
      }
    },

    async getProposal() {
      try {
        this.$vs.loading();

        const params = get(this.$router, "history.current.params", null);
        this.params = params;

        if (params && params.uuid) {
          try {
            // Buscando os dados da proposta usando o endpoint
            const response = await api.get(`api/v1/proposals/${params.uuid}`);

            if (response.data) {
              this.proposal = response.data;
              this.logo = response.data.company.logo_url;

              // Verifica o status da proposta
              if (response.data.status === "accepted") {
                this.currentStep = 4; // Vai para a tela de parabéns/aceite
              } else if (
                response.data.status === "refused" ||
                response.data.status === "rejected"
              ) {
                this.currentStep = 5; // Vai para a tela de agradecimento após recusa
              } else {
                this.currentStep = 1; // Vai para a tela de visualização da proposta normalmente
              }
            }
          } catch (err) {
            // Redireciona para página NotFound em caso de erro
            console.error("Erro ao buscar proposta:", err);
            this.$router.push("/recrutei/notfound");
          }
        } else {
          // Caso não tenha UUID, também redireciona para NotFound
          this.$router.push("/recrutei/notfound");
        }
      } catch (error) {
        console.error("Erro ao obter parâmetros:", error);
        this.$router.push("/recrutei/notfound");
      } finally {
        this.$vs.loading.close();
      }
    },

    async accessProposal() {
      if (!this.isFormValid) return;

      try {
        this.error = null;
        this.$vs.loading();

        // Validar código de acesso
        const response = await api.put(
          "api/v1/proposals/" + this.params.uuid + "/access",
          {
            code: this.accessCode,
          }
        );

        this.proposal = response.data;
        this.currentStep = 2;
      } catch (error) {
        this.error =
          "Código de acesso inválido. Por favor, verifique e tente novamente.";
        console.error(error);
      } finally {
        this.$vs.loading.close();
      }
    },

    showAcceptModal() {
      this.currentStep = 3;
    },

    showRejectModal() {
      this.rejectReason = "";
      this.currentStep = 6;
    },

    async acceptProposal() {
      if (!this.isReasonValid) return;

      try {
        this.$vs.loading();

        const response = await api.put(
          `api/v1/proposals/${this.params.uuid}/accept`,
          {
            response: this.acceptReason,
          }
        );

        this.currentStep = 4; // Sucesso
      } catch (error) {
        this.error = "Erro ao aceitar a proposta. Por favor, tente novamente.";
        console.error(error);
      } finally {
        this.$vs.loading.close();
      }
    },

    async rejectProposal() {
      if (!this.isRejectReasonValid) return;

      try {
        this.$vs.loading({
          text: "Enviando resposta...",
        });

        await api.put(`api/v1/proposals/${this.params.uuid}/refuse`, {
          response: this.rejectReason,
        });

        this.currentStep = 5; // Vai para a tela de agradecimento após recusa
      } catch (error) {
        this.error = "Erro ao recusar a proposta. Por favor, tente novamente.";
        console.error("Erro ao recusar proposta:", error);
      } finally {
        this.$vs.loading.close();
      }
    },

    cancel() {
      if (this.currentStep === 6) {
        this.currentStep = 2;
      } else {
        this.currentStep = 2;
      }
    },
  },
  created() {
    this.fetchProposal();
  },
};
</script>

<template style="display: flex">
  <div class="proposal-container">
    <!-- Componente de logo adaptável a cada passo -->
    <div class="logo-wrapper" :class="`logo-step-${currentStep}`">
      <img v-if="logo" :src="logo" alt="Logo da empresa" class="company-logo" />
    </div>

    <!-- Tela 1: Código de Acesso (acessar-proposta) -->
    <div v-if="currentStep === 1" class="access-screen">
      <div class="content-spacing">
        <h5 class="card-header">Carta proposta</h5>
        <h3 class="job-title">
          {{ (proposal && proposal.vacancy && proposal.vacancy.title) || "Proposta" }}
        </h3>

        <p class="intro-text">
          Olá {{ (proposal && proposal.talent && ((proposal.talent.prospect && proposal.talent.prospect.name) || (proposal.talent.user && proposal.talent.user.name))) || "Candidato" }},
          para visualizar a proposta informe o código de acesso recebido por
          e-mail.
        </p>

        <div class="access-form">
          <div class="form-group">
            <input
              v-model="accessCode"
              type="text"
              class="form-control"
              placeholder="Código de acesso"
            />
          </div>

          <div class="checkbox-group">
            <div class="form-check">
              <input
                v-model="privacyPolicy"
                type="checkbox"
                class="form-check-input"
                id="privacy"
              />
              <label class="form-check-label" for="privacy">
                Concordo com as Políticas de Privacidade
              </label>
            </div>

            <div class="form-check">
              <input
                v-model="termsOfUse"
                type="checkbox"
                class="form-check-input"
                id="terms"
              />
              <label class="form-check-label" for="terms">
                Concordo com os Termos de uso
              </label>
            </div>
          </div>

          <button
            @click="accessProposal"
            :disabled="!isFormValid"
            class="btn-access"
          >
            Acessar proposta
          </button>

          <div v-if="error" class="error-message">{{ error }}</div>
        </div>
      </div>
    </div>

    <!-- Tela 2: Leitura da Proposta (ler-proposta) -->
    <div v-if="currentStep === 2" class="proposal-details-screen">
      <div class="content-spacing">
        <h5 class="card-header">Carta proposta</h5>
        <h3 class="job-title">
          {{ (proposal && proposal.vacancy_title) || "Proposta" }}
        </h3>

        <p class="intro-text">
          {{ (proposal && proposal.message) || "Aqui estão os detalhes da proposta:" }}
        </p>

        <div class="proposal-info">
          <div class="info-row">
            <div class="info-label">Data de início</div>
            <div class="info-value">
              {{ formatDate(proposal && proposal.start_date) }}
            </div>
          </div>

          <div class="info-row">
            <div class="info-label">Salário</div>
            <div class="info-value">
              {{ (proposal && proposal.salary) || "Não Informado" }}
            </div>
          </div>

          <div
            class="info-row"
            v-if="proposal && proposal.benefits && proposal.benefits.length > 0"
          >
            <div class="info-label">Benefícios</div>
            <div class="info-value">
              {{ (proposal && proposal.benefits) || "Não Informado" }}
            </div>
          </div>

          <div class="info-row" v-if="proposal && proposal.regime">
            <div class="info-label">Regime de contratação</div>
            <div class="info-value">
              {{ (proposal && proposal.regime) || "Não Informado" }}
            </div>
          </div>

          <div class="info-row" v-if="proposal && proposal.workload">
            <div class="info-label">Carga horária semanal</div>
            <div class="info-value">
              {{ (proposal && proposal.workload) || "Não Informado" }}
            </div>
          </div>
        </div>

        <div class="pdf-item" v-if="proposal && proposal.attachment && proposal.attachment.url && proposal.attachment.name">
          <div class="pdf-info">
            <span class="pdf-name">{{ proposal.attachment.name }}</span>
          </div>
          <div class="pdf-actions">
            <a :href="proposal.attachment.url" target="_blank" rel="noopener noreferrer" class="pdf-action open-btn" title="Visualizar">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"
                  fill="#2196F3"
                />
              </svg>
            </a>
          </div>
        </div>

        <div class="action-buttons">
          <button @click="showAcceptModal" class="btn-accept">
            <svg
              class="check-icon"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                fill="white"
              />
            </svg>
            Aceitar
          </button>
          <button @click="showRejectModal" class="btn-reject">
            <svg
              class="prohibition-icon"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8 0-1.85.63-3.55 1.69-4.9L16.9 18.31C15.55 19.37 13.85 20 12 20zm6.31-3.1L7.1 5.69C8.45 4.63 10.15 4 12 4c4.42 0 8 3.58 8 8 0 1.85-.63 3.55-1.69 4.9z"
                fill="#666"
              />
            </svg>
            Recusar
          </button>
        </div>
      </div>
    </div>

    <!-- Tela 3: Modal de Aceitação (modal-aceite-recusa) -->
    <div v-if="currentStep === 3" class="modal-overlay">
      <div class="modal-card">
        <div class="modal-header">
          <div class="title-area">
            <div class="accept-icon">
              <svg
                width="19"
                height="19"
                viewBox="0 0 19 19"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.95565 12.8269L13.5787 7.22889L12.6499 6.30015L7.95565 10.9694L5.83065 8.8694L4.9019 9.79814L7.95565 12.8269ZM9.25165 18.6731C8.00432 18.6731 6.82757 18.4363 5.7214 17.9626C4.61523 17.489 3.64707 16.8372 2.8169 16.0074C1.98674 15.1776 1.33465 14.2098 0.860652 13.1041C0.386819 11.9985 0.149902 10.8221 0.149902 9.57489C0.149902 8.31089 0.386736 7.12998 0.860402 6.03214C1.33407 4.93431 1.98582 3.97031 2.81565 3.14014C3.64549 2.30998 4.61324 1.65789 5.7189 1.18389C6.82457 0.710061 8.00099 0.473145 9.24815 0.473145C10.5122 0.473145 11.6931 0.709977 12.7909 1.18364C13.8887 1.65731 14.8527 2.30906 15.6829 3.13889C16.5131 3.96873 17.1652 4.93231 17.6392 6.02964C18.113 7.12698 18.3499 8.30756 18.3499 9.57139C18.3499 10.8187 18.1131 11.9955 17.6394 13.1016C17.1657 14.2078 16.514 15.176 15.6842 16.0061C14.8543 16.8363 13.8907 17.4884 12.7934 17.9624C11.6961 18.4362 10.5155 18.6731 9.25165 18.6731ZM9.2499 17.3731C11.4166 17.3731 13.2582 16.6148 14.7749 15.0981C16.2916 13.5815 17.0499 11.7398 17.0499 9.57314C17.0499 7.40648 16.2916 5.56481 14.7749 4.04814C13.2582 2.53148 11.4166 1.77314 9.2499 1.77314C7.08324 1.77314 5.24157 2.53148 3.7249 4.04814C2.20824 5.56481 1.4499 7.40648 1.4499 9.57314C1.4499 11.7398 2.20824 13.5815 3.7249 15.0981C5.24157 16.6148 7.08324 17.3731 9.2499 17.3731Z"
                  fill="#309E3A"
                />
              </svg>
            </div>
            <h4 class="modal-title">Aceitando proposta</h4>
          </div>
          <button @click="cancel" class="close-btn">×</button>
        </div>

        <div class="modal-content">
          <p class="modal-text">
            A pessoa recrutadora será avisada que você aceitou a proposta.
          </p>

          <div class="form-group reason-field">
            <label class="reason-label"
              >Conte o motivo <span class="required">*</span></label
            >
            <textarea
              v-model="acceptReason"
              class="reason-input"
              placeholder="Ex.: Fiquei muito feliz por aceitar esta proposta. Os benefícios e o salário estão dentro das minhas expectativas e estou ansioso para começar a contribuir com a empresa."
            ></textarea>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="cancel" class="btn-cancel">Cancelar</button>
          <button
            @click="acceptProposal"
            :disabled="!isReasonValid"
            class="btn-confirm-accept"
          >
            Aceitar e enviar
          </button>
        </div>
      </div>
    </div>

    <!-- Tela 4: Confirmação de Aceitação (tela final de aceitacao) -->
    <div v-if="currentStep === 4" class="success-screen">
      <div class="success-card-aceite">
        <div class="success-icon">
          <lottie-player
            src="/check-animation.json"
            background="transparent"
            speed="1"
            style="width: 120px; height: 120px"
            autoplay
            :loop="false"
          >
          </lottie-player>
        </div>

        <h2 class="success-title">Parabéns!</h2>

        <p class="success-text">
          Estamos felizes em saber que você aceitou a proposta. Em breve, o
          recrutador entrará em contato para os próximos passos.
        </p>
      </div>
    </div>

    <!-- Tela de Rejeição (adicionada) -->
    <div v-if="currentStep === 5" class="rejection-screen">
      <div class="rejection-card">
        <div class="rejection-icon">
          <div class="paper-plane-circle">
            <lottie-player
              src="/message-sent-animation.json"
              background="transparent"
              speed="1"
              style="width: 100px; height: 100px"
              autoplay
              :loop="false"
            >
            </lottie-player>
          </div>
        </div>

        <h2 class="rejection-title">Obrigado por compartilhar seu feedback!</h2>

        <p class="rejection-text">
          Entendemos que cada decisão é importante. O recrutador poderá enviar
          uma nova proposta por e-mail, então fique atento(a) à sua caixa de
          entrada
        </p>
      </div>
    </div>

    <!-- Tela do Modal de Recusa -->
    <div v-if="currentStep === 6" class="modal-overlay">
      <div class="modal-card">
        <div class="modal-header reject-header">
          <div class="title-area">
            <i class="fa-regular fa-circle-xmark reject-icon">
              <svg
                width="19"
                height="19"
                viewBox="0 0 19 19"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9.25165 18.6536C8.00432 18.6536 6.82757 18.4168 5.7214 17.9431C4.61524 17.4694 3.64707 16.8177 2.8169 15.9879C1.98674 15.158 1.33465 14.1903 0.860652 13.0846C0.386819 11.9789 0.149902 10.8025 0.149902 9.55536C0.149902 8.29136 0.386736 7.11045 0.860402 6.01261C1.33407 4.91478 1.98582 3.95078 2.81565 3.12061C3.64549 2.29045 4.61324 1.63836 5.7189 1.16436C6.82457 0.69053 8.00099 0.453613 9.24815 0.453613C10.5122 0.453613 11.6931 0.690446 12.7909 1.16411C13.8887 1.63778 14.8527 2.28953 15.6829 3.11936C16.5131 3.9492 17.1652 4.91278 17.6392 6.01011C18.113 7.10745 18.3499 8.28803 18.3499 9.55186C18.3499 10.7992 18.1131 11.9759 17.6394 13.0821C17.1657 14.1883 16.514 15.1564 15.6842 15.9866C14.8543 16.8168 13.8907 17.4689 12.7934 17.9429C11.6961 18.4167 10.5155 18.6536 9.25165 18.6536ZM9.2499 17.3536C10.2047 17.3536 11.1082 17.1908 11.9604 16.8651C12.8124 16.5394 13.5813 16.0844 14.2672 15.4999L3.30365 4.53636C2.72549 5.2222 2.27199 5.99111 1.94315 6.84311C1.61432 7.69528 1.4499 8.59878 1.4499 9.55361C1.4499 11.7203 2.20824 13.5619 3.7249 15.0786C5.24157 16.5953 7.08324 17.3536 9.2499 17.3536ZM15.1962 14.5709C15.7807 13.885 16.2357 13.1161 16.5614 12.2641C16.8871 11.4119 17.0499 10.5084 17.0499 9.55361C17.0499 7.38695 16.2916 5.54528 14.7749 4.02861C13.2582 2.51195 11.4166 1.75361 9.2499 1.75361C8.29274 1.75361 7.38707 1.91486 6.5329 2.23736C5.6789 2.5597 4.91215 3.01636 4.23265 3.60736L15.1962 14.5709Z"
                  fill="#DE2348"
                />
              </svg>
            </i>
            <h4 class="modal-title">Recusando proposta</h4>
          </div>
          <button @click="cancel" class="close-btn">×</button>
        </div>

        <div class="modal-content">
          <p class="modal-text">
            A pessoa recrutadora será avisada que você recusou a proposta.
          </p>

          <div class="form-group reason-field">
            <label class="reason-label"
              >Conte o motivo <span class="required">*</span></label
            >
            <textarea
              v-model="rejectReason"
              class="reason-input reject-reason-input"
              placeholder="Ex.: Após analisar detalhadamente, a proposta não atende minhas expectativas salariais e as responsabilidades não estão alinhadas com meus objetivos de carreira atuais."
            ></textarea>
          </div>
        </div>

        <div class="modal-footer">
          <button
            @click="rejectProposal"
            :disabled="!isRejectReasonValid"
            class="btn-confirm-reject"
          >
            Recusar e enviar
          </button>
          <button @click="cancel" class="btn-cancel">Cancelar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.proposal-container {
  display: flex;
  padding: 20px 15px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  width: 100%;
  background-color: #f5f5f5;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen,
    Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
}

.logo-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;

  &.logo-step-1 {
    margin-bottom: 30px;
  }

  &.logo-step-2 {
    margin-bottom: 20px;
  }

  &.logo-step-3 {
    /* Estilos específicos para o logo no modal de aceitação */
    position: absolute;
    top: 20px;
    z-index: 1100; /* Acima do modal */
  }

  &.logo-step-4 {
    margin-bottom: 50px; /* Mais espaço na tela de confirmação */
  }
}

.company-logo {
  max-width: 150px;
  height: auto;
}

/* Nova classe para adicionar espaçamento sem fundo branco */
.content-spacing {
  padding: 30px;
  max-width: 550px;
  width: 100%;
  margin: 0 auto;
}

/* Card comum para todas as telas */
.card,
.modal-card,
.success-card {
  border-radius: 8px;
  background-color: #fff;
  padding: 30px;
  max-width: 550px;
  width: 100%;
  text-align: center;
}

.success-card-aceite {
  border-radius: 8px;
  padding: 30px;
  max-width: 550px;
  width: 100%;
  text-align: center;
}

.card-header {
  font-size: 22px;
  color: #333;
  margin: 0 0 10px;
  font-weight: 600;
}

.job-title {
  font-size: 18px;
  color: #555;
  margin: 0 0 30px;
  font-weight: 500;
}

.intro-text {
  color: #666;
  font-size: 15px;
  line-height: 1.5;
  margin-bottom: 35px;
}

/* Tela 1: Código de Acesso */
.access-form {
  display: flex;
  flex-direction: column;
  margin-top: 10px;
}

.form-group {
  margin-bottom: 30px;

  input {
    width: 100%;
    padding: 14px 15px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 16px;

    &:focus {
      border-color: #1c86ee;
      outline: none;
    }

    &::placeholder {
      color: #aaa;
    }
  }
}

.checkbox-group {
  margin-bottom: 30px;
}

.form-check {
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;
  text-align: left;

  input[type="checkbox"] {
    margin-right: 12px;
    margin-top: 3px;
  }

  label {
    font-size: 14px;
    color: #555;
    line-height: 1.5;
  }
}

.btn-access {
  background-color: #4caf50;
  color: white;
  padding: 14px 24px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
  margin-top: 10px;

  &:hover {
    background-color: #388e3c;
  }

  &:disabled {
    background-color: #a5d6a7;
    cursor: not-allowed;
  }

  .arrow-icon {
    margin-left: 8px;
  }
}

.error-message {
  color: #d32f2f;
  margin-top: 20px;
  font-size: 14px;
}

/* Tela 2: Leitura da Proposta */
.proposal-details-screen {
  width: 100%;
  max-width: 550px;
  margin: 0 auto;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.proposal-info {
  margin: 30px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.info-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-label {
  font-size: 14px;
  font-weight: 500;
  color: #666;
}

.info-value {
  font-size: 16px;
  color: #333;
  font-weight: 400;
}

.pdf-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 12px 16px;
  margin: 25px 0;
  width: 100%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.pdf-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pdf-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.pdf-name {
  color: #e53935;
  font-weight: 500;
  font-size: 15px;
}

.pdf-actions {
  display: flex;
  align-items: center;
  gap: 15px;
}

.pdf-action {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
}

.download-btn {
  .download-icon:after {
    content: "↓";
    font-size: 18px;
  }
}

.open-btn {
  .open-icon:after {
    content: "↗";
    font-size: 18px;
  }
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 30px;
  width: 100%;

  button {
    padding: 14px 0;
    border-radius: 4px;
    font-weight: 500;
    font-size: 16px;
    cursor: pointer;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
  }

  .btn-accept {
    background-color: #4caf50;
    color: white;
    border: none;

    &:hover {
      background-color: #388e3c;
    }
  }

  .btn-reject {
    background-color: white;
    color: #333;
    border: 1px solid #ddd;

    &:hover {
      background-color: #f5f5f5;

      .prohibition-icon path {
        fill: #d32f2f; /* Vermelho mais forte no hover */
      }
    }

    .prohibition-icon path {
      transition: fill 0.2s ease;
    }
  }
}

/* Tela 3: Modal de Aceitação */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.modal-card {
  border-radius: 8px;
  max-width: 550px;
  width: 90%;
  padding: 0;
  overflow: hidden;
  background-color: #fff;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;

  .title-area {
    display: flex;
    align-items: center;
  }

  .accept-icon {
    width: 20px;
    height: 20px;
    margin-right: 10px;
  }

  .modal-title {
    font-size: 16px;
    color: #333;
    margin: 0;
    font-weight: 600;
  }

  .close-btn {
    background: none;
    border: none;
    font-size: 24px;
    color: #777;
    cursor: pointer;

    &:hover {
      color: #333;
    }
  }
}

.modal-content {
  padding: 25px;

  .modal-text {
    text-align: left;
    color: #555;
    margin-bottom: 20px;
    font-size: 14px;
  }

  .reason-field {
    margin-bottom: 0;

    .reason-label {
      display: block;
      text-align: left;
      margin-bottom: 12px;
      font-weight: 500;
      color: #333;
      font-size: 14px;

      .required {
        color: #d32f2f;
      }
    }

    .reason-input {
      width: 100%;
      min-height: 100px;
      padding: 12px;
      border: 1px solid #ddd;
      border-radius: 4px;
      resize: vertical;
      font-family: inherit;
      font-size: 14px;

      &:focus {
        border-color: #1c86ee;
        outline: none;
      }
    }
  }
}

.modal-footer {
  padding: 20px 25px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  border-top: 1px solid #eee;

  .btn-confirm-accept {
    background-color: #4caf50;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    font-weight: 500;
    cursor: pointer;

    &:hover {
      background-color: #388e3c;
    }

    &:disabled {
      background-color: #a5d6a7;
      cursor: not-allowed;
    }
  }

  .btn-cancel {
    background: none;
    border: none;
    color: #555;
    padding: 8px 16px;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
}

/* Tela 4: Confirmação de Aceitação */
.success-screen {
  display: flex;
  justify-content: center;
  align-items: center;
}

.success-card {
  text-align: center;
  padding: 40px 30px;
}

.success-icon {
  margin: 0 auto 20px;
  display: flex;
  justify-content: center;
}

.success-title {
  font-size: 24px;
  color: #333;
  margin: 0 0 15px;
  font-weight: 600;
}

.success-text {
  color: #666;
  font-size: 16px;
  line-height: 1.5;
}

/* Tela de Rejeição */
.rejection-screen {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
}

.rejection-card {
  text-align: center;
  padding: 40px 30px;
  border-radius: 8px;
  max-width: 550px;
  width: 100%;
  background-color: #fff;
}

.rejection-icon {
  margin: 0 auto 30px;
  display: flex;
  justify-content: center;
}

.paper-plane-circle {
  width: 120px;
  height: 120px;
  background-color: #32c5ff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.rejection-title {
  font-size: 24px;
  color: #333;
  margin: 0 0 20px;
  font-weight: 600;
}

.rejection-text {
  color: #666;
  font-size: 16px;
  line-height: 1.6;
  margin: 0;
}

/* Tela do Modal de Recusa */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.modal-card {
  border-radius: 8px;
  max-width: 550px;
  width: 90%;
  padding: 0;
  overflow: hidden;
  background-color: #fff;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;

  .title-area {
    display: flex;
    align-items: center;
  }

  .reject-icon {
    width: 20px;
    height: 20px;
    margin-right: 10px;
  }

  .modal-title {
    font-size: 16px;
    color: #333;
    margin: 0;
    font-weight: 600;
  }

  .close-btn {
    background: none;
    border: none;
    font-size: 24px;
    color: #777;
    cursor: pointer;

    &:hover {
      color: #333;
    }
  }
}

.modal-content {
  padding: 25px;

  .modal-text {
    text-align: left;
    color: #555;
    margin-bottom: 20px;
    font-size: 14px;
  }

  .reason-field {
    margin-bottom: 0;

    .reason-label {
      display: block;
      text-align: left;
      margin-bottom: 12px;
      font-weight: 500;
      color: #333;
      font-size: 14px;

      .required {
        color: #d32f2f;
      }
    }

    .reason-input {
      width: 100%;
      min-height: 100px;
      padding: 12px;
      border: 1px solid #ddd;
      border-radius: 4px;
      resize: vertical;
      font-family: inherit;
      font-size: 14px;

      &:focus {
        border-color: #1c86ee;
        outline: none;
      }
    }
  }
}

.modal-footer {
  padding: 20px 25px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  border-top: 1px solid #eee;

  .btn-confirm-reject {
    background-color: #e91e63;
    color: white;
    border: none;
    padding: 12px 20px;
    border-radius: 4px;
    font-weight: 500;
    cursor: pointer;
    width: 100%;

    &:hover {
      background-color: #c2185b;
    }

    &:disabled {
      background-color: #f8bbd0;
      cursor: not-allowed;
    }
  }

  .btn-cancel {
    margin-top: 10px;
    color: #00bcd4;
    text-align: center;
  }
}

/* Estilos para o modal de recusa */
.reject-header {
  border-color: #f0f0f0;
}

.reject-icon {
  color: #e91e63;
  font-size: 20px;
  margin-right: 10px;
}

.reject-reason-input {
  border-color: #e91e63;
  border-width: 2px;

  &::placeholder {
    color: #e91e6380;
    font-style: italic;
  }

  &:focus {
    border-color: #e91e63;
    outline: none;
  }
}

.btn-confirm-reject {
  background-color: #e91e63;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  width: 100%;

  &:hover {
    background-color: #c2185b;
  }

  &:disabled {
    background-color: #f8bbd0;
    cursor: not-allowed;
  }
}

.modal-footer {
  border-top: none;
  flex-direction: column;
  padding-top: 0;

  .btn-cancel {
    margin-top: 10px;
    color: #00bcd4;
    text-align: center;
  }
}
</style>
